// src/pages/Gullveig.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  DollarSign, Package, Bell, History, Loader2, CheckCircle, X,
  Trash2, Search, RefreshCw
} from 'lucide-react';
import { Logo } from '../components/Logo';
import {
  getPendingCharges,
  getAllCharges,
  updateChargeStatus,
  clearNoticeHistory,
  getNoticeHistory,
  getPatronsWithNotices
} from '../services/chargeService';
import {
  addLostBooksToReorder,
  getAllLostBooks,
  markBookOrdered,
  markBookReceived,
  removeLostBook,
  clearAllLostBooks,
  getLostBookStats
} from '../services/lostBookService';

function Gullveig() {
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();

  // Tab state
  const [activeTab, setActiveTab] = useState('pending');

  // Data states
  const [pendingCharges, setPendingCharges] = useState([]);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [lostBooks, setLostBooks] = useState([]);
  const [lostBookStats, setLostBookStats] = useState(null);
  const [patronsWithNotices, setPatronsWithNotices] = useState([]);

  // UI states
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  // Payment modal state
  const [paymentModal, setPaymentModal] = useState(null);
  const [selectedBooksForReorder, setSelectedBooksForReorder] = useState([]);

  // Clear notices state
  const [noticeSearchQuery, setNoticeSearchQuery] = useState('');
  const [selectedPatronNotices, setSelectedPatronNotices] = useState(null);
  const [patronNoticeHistory, setPatronNoticeHistory] = useState([]);

  // Lost books filter
  const [lostBooksFilter, setLostBooksFilter] = useState('pending');

  // Load all data on mount
  const loadData = useCallback(async () => {
    if (!currentUser?.uid) return;
    setLoading(true);
    try {
      const [pending, all, lost, stats, patrons] = await Promise.all([
        getPendingCharges(currentUser.uid),
        getAllCharges(currentUser.uid),
        getAllLostBooks(currentUser.uid),
        getLostBookStats(currentUser.uid),
        getPatronsWithNotices(currentUser.uid)
      ]);

      setPendingCharges(pending);
      setPaymentHistory(all.filter(c => c.status === 'paid' || c.status === 'waived'));
      setLostBooks(lost);
      setLostBookStats(stats);
      setPatronsWithNotices(patrons);
    } catch (err) {
      console.error('Error loading Gullveig data:', err);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.uid]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter lost books based on selected filter
  const filteredLostBooks = lostBooks.filter(book => {
    if (lostBooksFilter === 'all') return true;
    return book.status === lostBooksFilter;
  });

  // Filter patrons for search
  const filteredPatrons = patronsWithNotices.filter(p =>
    p.patronName.toLowerCase().includes(noticeSearchQuery.toLowerCase())
  );

  // HANDLERS

  // Mark charge as paid - opens modal to select books for reorder
  const handleMarkPaid = (charge) => {
    setPaymentModal(charge);
    setSelectedBooksForReorder(charge.books.map(b => b.barcode)); // Default all selected
  };

  // Confirm payment and optionally add books to reorder
  const confirmMarkPaid = async () => {
    if (!paymentModal) return;
    setActionLoading('paid');
    try {
      // Update charge status
      await updateChargeStatus(currentUser.uid, paymentModal.id, 'paid');

      // Clear notice history
      await clearNoticeHistory(currentUser.uid, paymentModal.patronName);

      // Add selected books to reorder list
      if (selectedBooksForReorder.length > 0) {
        const booksToReorder = paymentModal.books.filter(b =>
          selectedBooksForReorder.includes(b.barcode)
        );
        await addLostBooksToReorder(
          currentUser.uid,
          paymentModal.id,
          booksToReorder,
          paymentModal.patronName,
          paymentModal.patronType
        );
      }

      // Refresh data
      await loadData();
      setPaymentModal(null);
      setSelectedBooksForReorder([]);
    } catch (err) {
      console.error('Error marking as paid:', err);
      alert('Failed to mark as paid. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  // Mark charge as waived (no reorder)
  const handleMarkWaived = async (charge) => {
    if (!window.confirm(`Mark ${charge.patronName}'s charge of $${charge.totalCost.toFixed(2)} as WAIVED?`)) return;
    setActionLoading(charge.id);
    try {
      await updateChargeStatus(currentUser.uid, charge.id, 'waived');
      await clearNoticeHistory(currentUser.uid, charge.patronName);
      await loadData();
    } catch (err) {
      console.error('Error marking as waived:', err);
      alert('Failed to mark as waived.');
    } finally {
      setActionLoading(null);
    }
  };

  // Mark as returned (books came back, no payment needed)
  const handleMarkReturned = async (charge) => {
    if (!window.confirm(`Mark ${charge.patronName} as RETURNED?\n\nThis means the books were returned - no charge will be collected and their notice history will be cleared.`)) return;
    setActionLoading(charge.id);
    try {
      // Use 'waived' status but could add 'returned' status if needed
      await updateChargeStatus(currentUser.uid, charge.id, 'waived');
      await clearNoticeHistory(currentUser.uid, charge.patronName);
      await loadData();
      alert(`${charge.patronName} marked as returned. Notice history cleared.`);
    } catch (err) {
      console.error('Error marking as returned:', err);
      alert('Failed to mark as returned.');
    } finally {
      setActionLoading(null);
    }
  };

  // Load notice history for selected patron
  const handleSelectPatronForNotices = async (patronName) => {
    setSelectedPatronNotices(patronName);
    try {
      const history = await getNoticeHistory(currentUser.uid, patronName);
      setPatronNoticeHistory(history);
    } catch (err) {
      console.error('Error loading notice history:', err);
    }
  };

  // Clear notices for patron
  const handleClearNotices = async (patronName) => {
    if (!window.confirm(`Clear all notice history for ${patronName}?\n\nThis is typically done when books are returned before being formally charged.`)) return;
    setActionLoading(patronName);
    try {
      await clearNoticeHistory(currentUser.uid, patronName);
      await loadData();
      setSelectedPatronNotices(null);
      setPatronNoticeHistory([]);
      alert(`Notice history cleared for ${patronName}.`);
    } catch (err) {
      console.error('Error clearing notices:', err);
      alert('Failed to clear notices.');
    } finally {
      setActionLoading(null);
    }
  };

  // Lost book actions
  const handleMarkOrdered = async (bookId) => {
    setActionLoading(bookId);
    try {
      await markBookOrdered(currentUser.uid, bookId);
      await loadData();
    } catch (err) {
      console.error('Error marking as ordered:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkReceived = async (bookId) => {
    setActionLoading(bookId);
    try {
      await markBookReceived(currentUser.uid, bookId);
      await loadData();
    } catch (err) {
      console.error('Error marking as received:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleRemoveLostBook = async (bookId, title) => {
    if (!window.confirm(`Remove "${title}" from the reorder list?`)) return;
    setActionLoading(bookId);
    try {
      await removeLostBook(currentUser.uid, bookId);
      await loadData();
    } catch (err) {
      console.error('Error removing book:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleClearAllLostBooks = async () => {
    const count = lostBooks.length;
    if (!window.confirm(`Clear ALL ${count} books from the reorder list?\n\nThis is typically done at the end of the school year. This action cannot be undone.`)) return;
    setActionLoading('clearAll');
    try {
      await clearAllLostBooks(currentUser.uid);
      await loadData();
      alert(`Cleared ${count} books from the reorder list.`);
    } catch (err) {
      console.error('Error clearing lost books:', err);
      alert('Failed to clear lost books.');
    } finally {
      setActionLoading(null);
    }
  };

  const tabs = [
    { id: 'pending', label: 'Pending Charges', icon: DollarSign, count: pendingCharges.length },
    { id: 'clearNotices', label: 'Clear Notices', icon: Bell, count: patronsWithNotices.length },
    { id: 'reorder', label: 'Lost Books', icon: Package, count: lostBookStats?.totalPending || 0 },
    { id: 'history', label: 'Payment History', icon: History, count: paymentHistory.length }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-600 to-yellow-600 text-white shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-0">
            <div className="flex items-center space-x-2 sm:space-x-4">
              <Logo className="w-12 h-12" />
              <div>
                <h1 className="text-lg sm:text-2xl font-bold">Gullveig</h1>
                <p className="text-amber-100 text-sm">Payment & Lost Book Management</p>
              </div>
            </div>
            <nav className="flex items-center space-x-3 sm:space-x-4">
              <button
                onClick={loadData}
                className="text-sm sm:text-base px-3 py-1.5 sm:px-4 sm:py-2 bg-amber-700 rounded-lg hover:bg-amber-800 transition flex items-center gap-2"
                disabled={loading}
              >
                <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
                Refresh
              </button>
              <button
                onClick={() => navigate('/asgard')}
                className="text-sm sm:text-base px-3 py-1.5 sm:px-4 sm:py-2 bg-amber-700 rounded-lg hover:bg-amber-800 transition"
              >
                Back to Asgard
              </button>
              <button
                onClick={logout}
                className="text-sm sm:text-base px-3 py-1.5 sm:px-4 sm:py-2 bg-red-600 rounded-lg hover:bg-red-700 transition"
              >
                Logout
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="container mx-auto px-4 py-4">
        <div className="flex gap-2 border-b border-gray-200 pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-t-lg transition ${
                activeTab === tab.id
                  ? 'bg-white border-2 border-b-0 border-amber-400 text-amber-700 font-semibold'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              <tab.icon size={18} />
              {tab.label}
              {tab.count > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 pb-8">
        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="animate-spin mx-auto mb-4" size={32} />
            <p className="text-gray-600">Loading payment data...</p>
          </div>
        ) : (
          <>
            {/* Pending Charges Tab */}
            {activeTab === 'pending' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-800">Pending Charges ({pendingCharges.length})</h2>

                {pendingCharges.length === 0 ? (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-8 text-center">
                    <CheckCircle className="mx-auto mb-4 text-green-500" size={48} />
                    <p className="text-green-700 font-medium">No pending charges!</p>
                    <p className="text-green-600 text-sm mt-1">All accounts are settled.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingCharges.map(charge => (
                      <div key={charge.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
                        <div className="flex justify-between items-start mb-3">
                          <div>
                            <h3 className="font-bold text-lg text-gray-800">{charge.patronName}</h3>
                            <p className="text-sm text-gray-500">
                              {charge.patronType} | Charged: {charge.chargeDate?.toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-2xl font-bold text-red-600">${charge.totalCost.toFixed(2)}</p>
                            <p className="text-xs text-gray-500">{charge.books?.length || 0} book(s)</p>
                          </div>
                        </div>

                        {/* Books list */}
                        <div className="bg-gray-50 rounded p-3 mb-3">
                          <p className="text-xs text-gray-500 mb-2 font-medium">Books:</p>
                          <div className="space-y-1">
                            {charge.books?.map((book, i) => (
                              <div key={i} className="flex justify-between text-sm">
                                <span className="text-gray-700">{book.title}</span>
                                <span className="text-gray-500">${(book.cost || 0).toFixed(2)}</span>
                              </div>
                            ))}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => handleMarkPaid(charge)}
                            disabled={actionLoading === charge.id}
                            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center gap-2 disabled:opacity-50"
                          >
                            {actionLoading === charge.id ? <Loader2 size={16} className="animate-spin" /> : <DollarSign size={16} />}
                            Mark Paid
                          </button>
                          <button
                            onClick={() => handleMarkWaived(charge)}
                            disabled={actionLoading === charge.id}
                            className="px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition flex items-center gap-2 disabled:opacity-50"
                          >
                            Waive Charge
                          </button>
                          <button
                            onClick={() => handleMarkReturned(charge)}
                            disabled={actionLoading === charge.id}
                            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-50"
                          >
                            Books Returned
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Clear Notices Tab */}
            {activeTab === 'clearNotices' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-800">Clear Notice History</h2>
                <p className="text-gray-600 text-sm">
                  Use this to clear notice history for patrons who returned their books before being formally charged.
                </p>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Search/List */}
                  <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
                    <div className="relative mb-4">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                      <input
                        type="text"
                        placeholder="Search patron name..."
                        value={noticeSearchQuery}
                        onChange={(e) => setNoticeSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      />
                    </div>

                    {patronsWithNotices.length === 0 ? (
                      <p className="text-gray-500 text-center py-4">No patrons with notice history.</p>
                    ) : (
                      <div className="max-h-96 overflow-y-auto space-y-2">
                        {filteredPatrons.map(patron => (
                          <button
                            key={patron.patronName}
                            onClick={() => handleSelectPatronForNotices(patron.patronName)}
                            className={`w-full text-left p-3 rounded-lg border transition ${
                              selectedPatronNotices === patron.patronName
                                ? 'bg-amber-50 border-amber-300'
                                : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                            }`}
                          >
                            <div className="flex justify-between items-center">
                              <span className="font-medium text-gray-800">{patron.patronName}</span>
                              <span className="text-sm text-gray-500">{patron.noticeCount} notice(s)</span>
                            </div>
                            <div className="flex gap-1 mt-1">
                              {patron.noticeTypes.map(type => (
                                <span
                                  key={type}
                                  className={`text-xs px-2 py-0.5 rounded ${
                                    type === 'final' ? 'bg-orange-100 text-orange-700' :
                                    type === 'charge' ? 'bg-red-100 text-red-700' :
                                    'bg-blue-100 text-blue-700'
                                  }`}
                                >
                                  {type}
                                </span>
                              ))}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Selected patron details */}
                  <div className="bg-white rounded-lg shadow-md border border-gray-200 p-4">
                    {selectedPatronNotices ? (
                      <>
                        <div className="flex justify-between items-center mb-4">
                          <h3 className="font-bold text-gray-800">{selectedPatronNotices}</h3>
                          <button
                            onClick={() => handleClearNotices(selectedPatronNotices)}
                            disabled={actionLoading === selectedPatronNotices}
                            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2 disabled:opacity-50"
                          >
                            {actionLoading === selectedPatronNotices ? (
                              <Loader2 size={16} className="animate-spin" />
                            ) : (
                              <Trash2 size={16} />
                            )}
                            Clear All Notices
                          </button>
                        </div>

                        <div className="space-y-2">
                          {patronNoticeHistory.map(notice => (
                            <div key={notice.id} className="bg-gray-50 rounded p-3 border border-gray-200">
                              <div className="flex justify-between items-start mb-2">
                                <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                                  notice.noticeType === 'final' ? 'bg-orange-100 text-orange-700' :
                                  notice.noticeType === 'charge' ? 'bg-red-100 text-red-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {notice.noticeType.toUpperCase()}
                                </span>
                                <span className="text-xs text-gray-500">
                                  {notice.dateSent?.toLocaleDateString()}
                                </span>
                              </div>
                              <p className="text-xs text-gray-600">
                                {notice.books?.length || 0} book(s) in notice
                              </p>
                            </div>
                          ))}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-12 text-gray-500">
                        <Bell className="mx-auto mb-4" size={48} />
                        <p>Select a patron to view their notice history</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Lost Books to Reorder Tab */}
            {activeTab === 'reorder' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-800">Lost Books to Reorder</h2>
                  {lostBooks.length > 0 && (
                    <button
                      onClick={handleClearAllLostBooks}
                      disabled={actionLoading === 'clearAll'}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition flex items-center gap-2 disabled:opacity-50"
                    >
                      {actionLoading === 'clearAll' ? (
                        <Loader2 size={16} className="animate-spin" />
                      ) : (
                        <Trash2 size={16} />
                      )}
                      Clear All (End of Year)
                    </button>
                  )}
                </div>

                {/* Stats */}
                {lostBookStats && (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-yellow-700">{lostBookStats.totalPending}</p>
                      <p className="text-sm text-yellow-600">Pending</p>
                      <p className="text-xs text-yellow-500">${lostBookStats.pendingCost.toFixed(2)}</p>
                    </div>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-blue-700">{lostBookStats.totalOrdered}</p>
                      <p className="text-sm text-blue-600">Ordered</p>
                      <p className="text-xs text-blue-500">${lostBookStats.orderedCost.toFixed(2)}</p>
                    </div>
                    <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                      <p className="text-2xl font-bold text-green-700">{lostBookStats.totalReceived}</p>
                      <p className="text-sm text-green-600">Received</p>
                    </div>
                  </div>
                )}

                {/* Filter */}
                <div className="flex gap-2">
                  {['pending', 'ordered', 'received', 'all'].map(filter => (
                    <button
                      key={filter}
                      onClick={() => setLostBooksFilter(filter)}
                      className={`px-3 py-1 rounded-lg text-sm capitalize transition ${
                        lostBooksFilter === filter
                          ? 'bg-amber-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {filter}
                    </button>
                  ))}
                </div>

                {/* Books list */}
                {filteredLostBooks.length === 0 ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                    <Package className="mx-auto mb-4 text-gray-400" size={48} />
                    <p className="text-gray-500">No books in this category.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredLostBooks.map(book => (
                      <div key={book.id} className={`bg-white rounded-lg shadow border p-4 ${
                        book.status === 'pending' ? 'border-yellow-200' :
                        book.status === 'ordered' ? 'border-blue-200' :
                        'border-green-200'
                      }`}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <h3 className="font-semibold text-gray-800">{book.title}</h3>
                            <p className="text-sm text-gray-500">
                              Barcode: {book.barcode} | Cost: ${book.cost?.toFixed(2)}
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Lost by: {book.patronName} | {book.dateLost?.toLocaleDateString()}
                            </p>
                            {book.orderedDate && (
                              <p className="text-xs text-blue-500 mt-1">
                                Ordered: {book.orderedDate.toLocaleDateString()}
                              </p>
                            )}
                            {book.receivedDate && (
                              <p className="text-xs text-green-500 mt-1">
                                Received: {book.receivedDate.toLocaleDateString()}
                              </p>
                            )}
                          </div>

                          <div className="flex flex-col gap-2">
                            <span className={`text-xs px-2 py-1 rounded font-medium text-center ${
                              book.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                              book.status === 'ordered' ? 'bg-blue-100 text-blue-700' :
                              'bg-green-100 text-green-700'
                            }`}>
                              {book.status.toUpperCase()}
                            </span>

                            {book.status === 'pending' && (
                              <button
                                onClick={() => handleMarkOrdered(book.id)}
                                disabled={actionLoading === book.id}
                                className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                              >
                                {actionLoading === book.id ? '...' : 'Mark Ordered'}
                              </button>
                            )}

                            {book.status === 'ordered' && (
                              <button
                                onClick={() => handleMarkReceived(book.id)}
                                disabled={actionLoading === book.id}
                                className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                              >
                                {actionLoading === book.id ? '...' : 'Mark Received'}
                              </button>
                            )}

                            <button
                              onClick={() => handleRemoveLostBook(book.id, book.title)}
                              disabled={actionLoading === book.id}
                              className="px-3 py-1 text-xs bg-red-100 text-red-600 rounded hover:bg-red-200 disabled:opacity-50"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Payment History Tab */}
            {activeTab === 'history' && (
              <div className="space-y-4">
                <h2 className="text-xl font-bold text-gray-800">Payment History ({paymentHistory.length})</h2>

                {paymentHistory.length === 0 ? (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 text-center">
                    <History className="mx-auto mb-4 text-gray-400" size={48} />
                    <p className="text-gray-500">No payment history yet.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {paymentHistory.map(charge => (
                      <div key={charge.id} className={`bg-white rounded-lg shadow border p-4 ${
                        charge.status === 'paid' ? 'border-green-200' : 'border-yellow-200'
                      }`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-semibold text-gray-800">{charge.patronName}</h3>
                            <p className="text-sm text-gray-500">
                              {charge.patronType} | {charge.books?.length || 0} book(s)
                            </p>
                            <p className="text-xs text-gray-400 mt-1">
                              Charged: {charge.chargeDate?.toLocaleDateString()} |
                              {charge.status === 'paid' ? ' Paid' : ' Waived'}: {charge.paidDate?.toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-xl font-bold text-gray-700">${charge.totalCost.toFixed(2)}</p>
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              charge.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {charge.status.toUpperCase()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Payment Modal */}
      {paymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Mark as Paid</h3>
              <button
                onClick={() => setPaymentModal(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                <X size={24} />
              </button>
            </div>

            <p className="text-gray-600 mb-4">
              <strong>{paymentModal.patronName}</strong> is paying <strong>${paymentModal.totalCost.toFixed(2)}</strong>
            </p>

            <div className="mb-4">
              <p className="font-medium text-gray-700 mb-2">
                Select books to add to the Lost Books Reorder list:
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {paymentModal.books?.map((book, i) => (
                  <label
                    key={i}
                    className="flex items-center gap-3 p-2 bg-gray-50 rounded hover:bg-gray-100 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedBooksForReorder.includes(book.barcode)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedBooksForReorder([...selectedBooksForReorder, book.barcode]);
                        } else {
                          setSelectedBooksForReorder(selectedBooksForReorder.filter(b => b !== book.barcode));
                        }
                      }}
                      className="w-4 h-4 text-amber-600"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-800">{book.title}</p>
                      <p className="text-xs text-gray-500">${(book.cost || 0).toFixed(2)}</p>
                    </div>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {selectedBooksForReorder.length} of {paymentModal.books?.length} selected for reorder
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => setPaymentModal(null)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                Cancel
              </button>
              <button
                onClick={confirmMarkPaid}
                disabled={actionLoading === 'paid'}
                className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {actionLoading === 'paid' ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <CheckCircle size={16} />
                )}
                Confirm Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Gullveig;
